import { CompanyResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateAllCompaniesCache } from '../queries/company-cache';

type UseEnableSmsActionProps = {
    companyId: string;
    /**
     * Runs when SMS could not be turned on because the company has no card on
     * file for it, so the caller can send the user off to add one. The API
     * refuses until they do, and nothing about the company says so in advance.
     */
    onPaymentMethodRequired?: () => void;
};

type UseEnableSmsActionData = {
    enableSms: () => Promise<CompanyResponse>;
    loading: boolean;
    error: string;
};

/**
 * Turning on SMS notifications for the company.
 *
 * Carries no `isAllowed` of its own: the billing tab is gated as a whole by
 * `canEditBilling` on the billing settings page, and everything on it is
 * reachable only from there.
 */
export function useEnableSmsAction(
    props: UseEnableSmsActionProps
): UseEnableSmsActionData {
    const { companyId, onPaymentMethodRequired } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/companies/{id}/sms/enable` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'NO_SMS_PAYMENT_METHOD')) {
                return t('actions.enable_sms.error_no_payment_method');
            }
            if (isApiError(error, 'SMS_ALREADY_ENABLED')) {
                return t('actions.enable_sms.error_already_enabled');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('shared.errors.company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.companies
                .enableSms(companyId)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllCompaniesCache(queryClient, data.data.company);

            UIToast.success(t('actions.enable_sms.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));

            // Not a dead end: the company can add a card and come back, so the
            // caller is told rather than left with the toast alone.
            if (isApiError(error, 'NO_SMS_PAYMENT_METHOD')) {
                onPaymentMethodRequired?.();
            }
        }
    });

    return {
        enableSms: (): Promise<CompanyResponse> => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
